import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  cloneForceGraphData,
  createMultiSeedRadialForce,
  EDGE_LABEL_ZOOM,
  ENTITY_TYPE_COLORS,
  FOCUS_RING_COLOR,
  formatLinkLabel,
  linkDistanceForWeight,
  linkStrokeStyle,
  seedMultiSeedRingPositions,
} from '../services/graphApi';

const TYPE_CLASS = {
  ORG: 'memetic-node-org',
  PERSON: 'memetic-node-person',
  GPE: 'memetic-node-gpe',
  EVENT: 'memetic-node-event',
};

const RING_RADIUS_2D = 90;
const SEED_RING = 48;
const DIM_NODE_ALPHA = 0.18;
const DIM_LINK_ALPHA = 0.06;

/** World-space sizes — zoom scales boxes/text on screen (do not divide by globalScale). */
const FONT_SEED = 5.2;
const FONT_NODE = 4.4;
const PAD_X = 4.5;
const PAD_Y = 3.2;
const MAX_LABEL_WIDTH = 72;
const MAX_LINES = 4;
const MIN_BOX_W = 28;
const MIN_BOX_H = 16;

function fontSizeForNode(node) {
  const base = (node.hop ?? 0) === 0 ? FONT_SEED : FONT_NODE;
  const bump = Math.min(Math.sqrt(Math.max(node.articleCount || 1, 1)) * 0.25, 1.4);
  return base + bump;
}

/**
 * Wrap label to maxWidth; grow line count up to MAX_LINES (no ellipsis cull).
 * Oversized single tokens keep their full width so the box can autofit.
 */
function wrapLabel(ctx, text, maxWidth) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = words[i];
      if (lines.length >= MAX_LINES - 1) {
        // Remainder on last line (box will widen if needed).
        const rest = [line, ...words.slice(i + 1)].join(' ');
        lines.push(rest);
        return lines;
      }
    }
  }
  lines.push(line);
  return lines;
}

function measureBlock(ctx, node, { isSeed = false } = {}) {
  const fontSize = fontSizeForNode(node);
  ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
  const lines = wrapLabel(ctx, node.name || '', MAX_LABEL_WIDTH);
  let textW = 0;
  lines.forEach((line) => {
    textW = Math.max(textW, ctx.measureText(line).width);
  });
  const lineH = fontSize * 1.2;
  const padX = isSeed ? PAD_X + 1.5 : PAD_X;
  const padY = isSeed ? PAD_Y + 1 : PAD_Y;
  const w = Math.max(MIN_BOX_W, textW + padX * 2);
  const h = Math.max(MIN_BOX_H, lines.length * lineH + padY * 2);
  return { fontSize, lines, lineH, w, h, radius: Math.min(h * 0.28, 5) };
}

function drawCenterPill(ctx, node, boxHeight) {
  const pill = 'CENTER';
  const pillFont = 3.2;
  ctx.font = `600 ${pillFont}px "IBM Plex Sans", system-ui, sans-serif`;
  const pillW = ctx.measureText(pill).width + 4;
  const pillH = pillFont + 2.4;
  const pillX = node.x - pillW / 2;
  const pillY = node.y - boxHeight / 2 - pillH - 2;
  ctx.fillStyle = 'rgba(125, 211, 252, 0.92)';
  ctx.fillRect(pillX, pillY, pillW, pillH);
  ctx.fillStyle = '#0b1220';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(pill, node.x, pillY + pillH / 2);
}

function endpointId(endpoint) {
  if (endpoint == null) {
    return null;
  }
  return String(typeof endpoint === 'object' ? endpoint.id : endpoint);
}

function configureForces(graphRef) {
  const fg = graphRef.current;
  if (!fg) {
    return;
  }

  const linkForce = fg.d3Force('link');
  if (linkForce) {
    linkForce.distance((link) => linkDistanceForWeight(link.weight));
  }

  const chargeForce = fg.d3Force('charge');
  if (chargeForce) {
    chargeForce.strength(-110);
    if (typeof chargeForce.distanceMax === 'function') {
      chargeForce.distanceMax(RING_RADIUS_2D * 4);
    }
  }

  fg.d3Force('radial', createMultiSeedRadialForce({
    ringRadius: RING_RADIUS_2D,
    seedRing: SEED_RING,
    strength: 0.32,
  }));
  fg.d3ReheatSimulation?.();
}

/**
 * Constellation (MS-VIZ-01) — block/pill nodes + multi-seed rings.
 * Do not use for Starburst; leave MemeticGraphCanvas untouched.
 */
const MemeticConstellationCanvas = forwardRef(({
  graphData,
  seedIds = [],
  onNodeClick,
  reducedMotion = false,
  layoutKey = 0,
  focusNodeIds = null,
}, ref) => {
  const graphRef = useRef(null);
  const hostRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [highlightNodes, setHighlightNodes] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);

  const seedKeySet = useMemo(
    () => new Set((seedIds || []).map((id) => String(id))),
    [seedIds],
  );

  /** External focus (e.g. Coverage timeline hover) reuses the canvas hover dim treatment. */
  const focusHighlight = useMemo(() => {
    const keys = (focusNodeIds || []).map((id) => String(id)).filter(Boolean);
    return keys.length > 0 ? new Set(keys) : null;
  }, [focusNodeIds]);

  const activeHighlight = highlightNodes ?? focusHighlight;

  const data = useMemo(() => cloneForceGraphData(graphData), [graphData]);

  const adjacency = useMemo(() => {
    const map = new Map();
    data.nodes.forEach((node) => map.set(String(node.id), new Set()));
    data.links.forEach((link) => {
      const s = endpointId(link.source);
      const t = endpointId(link.target);
      if (map.has(s) && map.has(t)) {
        map.get(s).add(t);
        map.get(t).add(s);
      }
    });
    return map;
  }, [data]);

  const buildNeighborhood = useCallback((...ids) => {
    const set = new Set();
    ids.filter(Boolean).forEach((id) => {
      const key = String(id);
      set.add(key);
      (adjacency.get(key) || []).forEach((neighbor) => set.add(neighbor));
    });
    return set.size > 0 ? set : null;
  }, [adjacency]);

  const handleNodeHover = useCallback((node) => {
    if (!node || seedKeySet.has(String(node.id)) || (node.hop ?? 0) < 1) {
      setHighlightNodes(null);
      return;
    }
    setHighlightNodes(buildNeighborhood(node.id));
  }, [buildNeighborhood, seedKeySet]);

  const handleLinkHover = useCallback((link) => {
    if (!link) {
      setHighlightNodes(null);
      setHoveredLink(null);
      return;
    }
    setHoveredLink(link);
    setHighlightNodes(buildNeighborhood(endpointId(link.source), endpointId(link.target)));
  }, [buildNeighborhood]);

  const graphKey = useMemo(
    () => `${layoutKey}:${data.nodes.length}:${data.links.length}:c:${[...seedKeySet].join(',')}`,
    [layoutKey, data.nodes.length, data.links.length, seedKeySet],
  );

  const ready = dimensions.width > 0 && dimensions.height > 0;

  const fitGraph = useCallback(() => {
    if (!graphRef.current || !data.nodes.length) {
      return;
    }
    if (data.nodes.length === 1) {
      const node = data.nodes[0];
      node.fx = 0;
      node.fy = 0;
      graphRef.current.centerAt(0, 0, 300);
      graphRef.current.zoom(1.8, 300);
      return;
    }
    graphRef.current.zoomToFit(500, 64);
  }, [data.nodes]);

  useImperativeHandle(ref, () => ({ fitGraph }), [fitGraph]);

  useEffect(() => {
    const element = hostRef.current;
    if (!element) {
      return undefined;
    }

    const measure = () => {
      const rect = element.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width > 0 && height > 0) {
        setDimensions((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [layoutKey, graphKey]);

  useEffect(() => {
    setHighlightNodes(null);
    setHoveredLink(null);
  }, [data]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }
    // Spread hop-0 seeds so they do not start stacked.
    seedMultiSeedRingPositions(data.nodes, SEED_RING);
    configureForces(graphRef);
    return undefined;
  }, [graphKey, ready, data.nodes]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }

    const delays = reducedMotion ? [50, 150] : [200, 800, 1600, 2800];
    const timers = delays.map((delay) => setTimeout(() => fitGraph(), delay));
    return () => timers.forEach(clearTimeout);
  }, [graphKey, ready, data.nodes.length, fitGraph, reducedMotion]);

  const handleEngineStop = useCallback(() => {
    if (data.nodes.length > 0) {
      fitGraph();
    }
  }, [data.nodes.length, fitGraph]);

  const paintNode = useCallback((node, ctx) => {
    if (node.x == null || node.y == null) {
      return;
    }
    const dimmed = activeHighlight != null && !activeHighlight.has(String(node.id));
    const focused = focusHighlight != null && focusHighlight.has(String(node.id));
    const isSeed = seedKeySet.has(String(node.id)) || (node.hop ?? 0) === 0;
    const { fontSize, lines, lineH, w, h, radius } = measureBlock(ctx, node, { isSeed });
    const color = ENTITY_TYPE_COLORS[node.type] || '#9aa5b1';

    // Cache world-space bounds for pointer hit testing.
    node.__boxW = w;
    node.__boxH = h;

    ctx.globalAlpha = dimmed ? DIM_NODE_ALPHA : 1;
    ctx.fillStyle = color;
    ctx.beginPath();
    const x = node.x - w / 2;
    const y = node.y - h / 2;
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
    ctx.fill();

    if (isSeed) {
      ctx.strokeStyle = '#7dd3fc';
      ctx.lineWidth = 2.6;
      ctx.stroke();
      // Outer halo so anchors read clearly among blocks.
      ctx.strokeStyle = 'rgba(125, 211, 252, 0.55)';
      ctx.lineWidth = 4;
      ctx.beginPath();
      const hx = node.x - (w + 4) / 2;
      const hy = node.y - (h + 4) / 2;
      const hr = Math.min((h + 4) * 0.28, 6);
      ctx.moveTo(hx + hr, hy);
      ctx.arcTo(hx + w + 4, hy, hx + w + 4, hy + h + 4, hr);
      ctx.arcTo(hx + w + 4, hy + h + 4, hx, hy + h + 4, hr);
      ctx.arcTo(hx, hy + h + 4, hx, hy, hr);
      ctx.arcTo(hx, hy, hx + w + 4, hy, hr);
      ctx.closePath();
      ctx.stroke();
    }

    if (focused) {
      ctx.strokeStyle = FOCUS_RING_COLOR;
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      const fx = node.x - (w + 6) / 2;
      const fy = node.y - (h + 6) / 2;
      const fr = Math.min((h + 6) * 0.28, 6);
      ctx.moveTo(fx + fr, fy);
      ctx.arcTo(fx + w + 6, fy, fx + w + 6, fy + h + 6, fr);
      ctx.arcTo(fx + w + 6, fy + h + 6, fx, fy + h + 6, fr);
      ctx.arcTo(fx, fy + h + 6, fx, fy, fr);
      ctx.arcTo(fx, fy, fx + w + 6, fy, fr);
      ctx.closePath();
      ctx.stroke();
    }

    if (!dimmed) {
      if (isSeed && seedKeySet.size === 1) {
        drawCenterPill(ctx, node, h);
      }
      ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#0b1220';
      const startY = node.y - ((lines.length - 1) * lineH) / 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, node.x, startY + i * lineH);
      });
    }
    ctx.globalAlpha = 1;
  }, [activeHighlight, focusHighlight, seedKeySet]);

  const paintNodePointerArea = useCallback((node, color, ctx) => {
    if (node.x == null || node.y == null) {
      return;
    }
    const w = node.__boxW || MIN_BOX_W;
    const h = node.__boxH || MIN_BOX_H;
    const pad = 5;
    ctx.fillStyle = color;
    ctx.fillRect(node.x - w / 2 - pad, node.y - h / 2 - pad, w + pad * 2, h + pad * 2);
  }, []);

  const paintLink = useCallback((link, ctx, globalScale) => {
    const source = link.source;
    const target = link.target;
    if (source?.x == null || source?.y == null || target?.x == null || target?.y == null) {
      return;
    }
    const active = activeHighlight == null
      || (activeHighlight.has(endpointId(source)) && activeHighlight.has(endpointId(target)));
    const weight = Math.max(link.weight || 1, 1);
    const stroke = linkStrokeStyle(link);
    ctx.globalAlpha = active ? 1 : DIM_LINK_ALPHA;
    ctx.strokeStyle = stroke.color;
    ctx.setLineDash(stroke.dash.map((n) => n / Math.max(globalScale, 0.35)));
    ctx.lineWidth = Math.min(0.6 + weight * 0.35, 2.8) / Math.max(globalScale, 0.35);
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.setLineDash([]);

    const midX = (source.x + target.x) / 2;
    const midY = (source.y + target.y) / 2;
    const zoomShow = globalScale > EDGE_LABEL_ZOOM && link.kind === 'RELATED' && active;
    const hoverShow = hoveredLink === link && active;
    if (zoomShow || hoverShow) {
      const label = formatLinkLabel(link, { detailed: hoverShow });
      const fontSize = hoverShow ? 5.2 : 4.2;
      ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const pad = 2.2;
      const metrics = ctx.measureText(label);
      const boxW = metrics.width + pad * 2;
      const boxH = fontSize + pad * 2;
      ctx.fillStyle = hoverShow ? 'rgba(12, 16, 22, 0.9)' : 'rgba(12, 16, 22, 0.72)';
      ctx.fillRect(midX - boxW / 2, midY - boxH / 2, boxW, boxH);
      ctx.fillStyle = '#e8edf2';
      ctx.fillText(label, midX, midY);
    }
    ctx.globalAlpha = 1;
  }, [activeHighlight, hoveredLink]);

  const paintLinkPointerArea = useCallback((link, color, ctx) => {
    const source = link.source;
    const target = link.target;
    if (source?.x == null || source?.y == null || target?.x == null || target?.y == null) {
      return;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
  }, []);

  if (!data.nodes.length) {
    return (
      <div className="memetic-canvas-empty">
        {seedKeySet.size > 1
          ? 'No constellation nodes for these seed entities in this window — try widening the time range or clearing filters.'
          : 'No graph nodes in this window — try another entity or widen the time range.'}
      </div>
    );
  }

  return (
    <div className="memetic-force-graph-host" ref={hostRef}>
      {ready && (
        <ForceGraph2D
          key={graphKey}
          ref={graphRef}
          graphData={data}
          width={dimensions.width}
          height={dimensions.height}
          backgroundColor="transparent"
          nodeRelSize={1}
          linkDirectionalParticles={0}
          cooldownTicks={reducedMotion ? 0 : 120}
          warmupTicks={reducedMotion ? 0 : 40}
          enableNodeDrag={!reducedMotion}
          enablePanInteraction
          enableZoomInteraction
          onEngineStop={handleEngineStop}
          onNodeClick={(node, event) => onNodeClick?.(node, event)}
          onNodeHover={handleNodeHover}
          onLinkHover={handleLinkHover}
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={paintNodePointerArea}
          linkCanvasObject={paintLink}
          linkCanvasObjectMode={() => 'replace'}
          linkPointerAreaPaint={paintLinkPointerArea}
        />
      )}
    </div>
  );
});

MemeticConstellationCanvas.displayName = 'MemeticConstellationCanvas';

export { TYPE_CLASS };
export default MemeticConstellationCanvas;
